import {
  ChatSessionResponse,
  ChatSessionWithMessagesResponse,
  ChatMessageResponse,
  ConversationConfig,
  CreateChatSessionRequest,
  UpdateChatSessionRequest,
  AppendChatMessageRequest,
  UpdateChatMessageRequest,
} from '@karehero/models';

import { mergeChatSessions } from 'utils/chatSessionMerge';

import api from '.';

interface CareCircleParam {
  careCircleId: string;
}

interface SessionParam extends CareCircleParam {
  sessionId: string;
}

interface MessageParam extends SessionParam {
  messageId: string;
}

export const chatSessionApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listConversationConfigs: builder.query<ConversationConfig[], void>({
      query: () => 'conversation-configs',
    }),

    listChatSessions: builder.query<
      ChatSessionWithMessagesResponse[],
      // Every mode's sessions without a mode.
      CareCircleParam & { mode?: string; status?: string }
    >({
      query: ({ careCircleId, mode, status }) => {
        const params = new URLSearchParams();
        if (mode) params.set('mode', mode);
        if (status) params.set('status', status);
        return `care-circle/${careCircleId}/chat-session?${params.toString()}`;
      },
      providesTags: (_result, _error, { careCircleId }) => [
        { type: 'ChatSession', id: careCircleId },
      ],
      // A refetch would otherwise drop messages still waiting to be saved.
      merge: (shown, fetched) => mergeChatSessions(shown, fetched),
    }),

    getChatSession: builder.query<
      ChatSessionWithMessagesResponse,
      SessionParam
    >({
      query: ({ careCircleId, sessionId }) =>
        `care-circle/${careCircleId}/chat-session/${sessionId}`,
      providesTags: (_result, _error, { sessionId }) => [
        { type: 'ChatSession', id: sessionId },
      ],
    }),

    createChatSession: builder.mutation<
      ChatSessionResponse,
      CareCircleParam & { body: CreateChatSessionRequest }
    >({
      query: ({ careCircleId, body }) => ({
        url: `care-circle/${careCircleId}/chat-session`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { careCircleId }) => [
        { type: 'ChatSession', id: careCircleId },
      ],
    }),

    updateChatSession: builder.mutation<
      void,
      SessionParam & { body: UpdateChatSessionRequest }
    >({
      query: ({ careCircleId, sessionId, body }) => ({
        url: `care-circle/${careCircleId}/chat-session/${sessionId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { careCircleId, sessionId }) => [
        { type: 'ChatSession', id: careCircleId },
        { type: 'ChatSession', id: sessionId },
      ],
    }),

    appendChatMessage: builder.mutation<
      ChatMessageResponse,
      SessionParam & { body: AppendChatMessageRequest }
    >({
      query: ({ careCircleId, sessionId, body }) => ({
        url: `care-circle/${careCircleId}/chat-session/${sessionId}/message`,
        method: 'POST',
        body,
      }),
      // The session list holds each session's messages, so it is stale too.
      invalidatesTags: (_result, _error, { careCircleId, sessionId }) => [
        { type: 'ChatSession', id: careCircleId },
        { type: 'ChatSession', id: sessionId },
      ],
    }),

    updateChatMessage: builder.mutation<
      ChatMessageResponse,
      MessageParam & { body: UpdateChatMessageRequest }
    >({
      query: ({ careCircleId, sessionId, messageId, body }) => ({
        url: `care-circle/${careCircleId}/chat-session/${sessionId}/message/${messageId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { careCircleId, sessionId }) => [
        { type: 'ChatSession', id: careCircleId },
        { type: 'ChatSession', id: sessionId },
      ],
    }),
  }),
});

export const invalidateChatSessionTags = () =>
  chatSessionApi.util.invalidateTags(['ChatSession']);

export const {
  useListConversationConfigsQuery,
  useListChatSessionsQuery,
  useGetChatSessionQuery,
  useCreateChatSessionMutation,
  useUpdateChatSessionMutation,
  useAppendChatMessageMutation,
  useUpdateChatMessageMutation,
} = chatSessionApi;
